import React from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import CalciteLabel from '@/components/shared/CalciteLabel';
import CalciteCheckbox from '@/components/shared/CalciteCheckbox';
import CalciteSelect from '@/components/shared/CalciteSelect';
import CalciteInputMessage from '@/components/shared/CalciteInputMessage';
import CalciteInputNumber from '@/components/shared/CalciteInputNumber';
import CalciteOption from '@/components/shared/CalciteOption';
import {useFormatPrice} from '@/hooks/useFormatPrice';
import {VALIDATION_ERROR_CODES} from '@/features/product/product-constants';

/**
 * Modal body content for the training-voucher product type.
 * @param {Object} props
 * @param {Array} props.products - Available products from pricing data
 * @param {Object} props.state - Current form state ({ quantity, policyAccepted, selectedProduct })
 * @param {Function} props.onChange - State updater — receives the full next state object
 * @param {Object} props.labels - AEM-authored label strings
 * @param {Object} props.currency - Currency formatting info from locale data
 * @param {string} props.formattedLocale - Formatted locale string for price formatting (e.g. "en-US")
 * @param {Object} props.formState - Structured form state from useFormState (valid, dirty, fields)
 * @param {string} props.defaultProduct - SKU of the default product to select when the modal opens (optional)
 * @returns {JSX.Element}
 */
const TrainingVoucherContent = ({
  products,
  currency,
  formattedLocale,
  state,
  onChange,
  labels,
  formState,
  defaultProduct,
}) => {
  const {quantity, policyAccepted, selectedProduct} = state;
  const selectedProductData = products?.find((p) => p.sku === selectedProduct) ?? null;
  const unitPrice = selectedProductData?.amount ?? 0;
  const formattedUnitPrice = useFormatPrice(unitPrice, {currency, formattedLocale});
  const totalPrice = quantity * unitPrice;
  const formattedTotalPrice = useFormatPrice(totalPrice, {currency, formattedLocale, omitDecimalsIfWhole: true});

  const handleQuantityInput = (e) => {
    let val = Number.parseInt(e.target.value, 10);
    if (!Number.isNaN(val) && val > 99) {
      e.target.value = '99';
      val = 99;
    }
    onChange((prev) => ({...prev, quantity: Number.isNaN(val) || val < 0 ? 0 : val}));
  };

  const handlePolicyChange = (e) => {
    const {checked} = e.target;
    onChange((prev) => ({...prev, policyAccepted: checked}));
  };

  const handleExamLevelChange = (e) => {
    onChange((prev) => ({...prev, selectedProduct: e.target.value || null}));
  };

  return (
    <>
      <p className="trainingvouchers-modal__content-heading">{labels?.contentHeading}</p>
      <hr className="trainingvouchers-modal__divider" />
      <div className="trainingvouchers-modal__pricing-grid">
        <div className="trainingvouchers-modal__exam-level">
          <CalciteLabel for="exam-level">{labels?.examLevel}</CalciteLabel>
          <CalciteSelect
            id="exam-level"
            className={`trainingvouchers-modal__exam-level-select ${formState.fields?.selectedProduct?.errors?.[VALIDATION_ERROR_CODES.REQUIRED] && formState.fields?.selectedProduct?.dirty ? ' trainingvouchers-modal__exam-level-select--invalid' : ''}`}
            scale="m"
            onSelectChange={handleExamLevelChange}
          >
            <CalciteOption value="" disabled selected={!selectedProduct} hidden={true}>
              {labels?.selectExamLevel}
            </CalciteOption>
            {products?.map((p) => (
              <CalciteOption key={p.sku} value={p.sku} selected={selectedProduct === p.sku}>
                {p.name}
              </CalciteOption>
            ))}
          </CalciteSelect>
        </div>
        <div className="trainingvouchers-modal__quantity">
          <CalciteLabel for="exam-quantity">{labels?.quantity}</CalciteLabel>
          <CalciteInputNumber
            id="exam-quantity"
            className="trainingvouchers-modal__quantity-input-field"
            min="0"
            max="99"
            status={
              formState.fields?.quantity?.errors?.[VALIDATION_ERROR_CODES.MIN] && formState.fields?.quantity?.dirty
                ? 'invalid'
                : null
            }
            value={String(quantity)}
            onInput={handleQuantityInput}
          />
        </div>
        <div className="trainingvouchers-modal__price">
          <span className="trainingvouchers-modal__price-currency">{currency?.currencyCode}</span>
          <span className="trainingvouchers-modal__price-amount">{formattedUnitPrice}</span>
        </div>
        <div className="trainingvouchers-modal__messages">
          <CalciteInputMessage scale="s" className="trainingvouchers-modal__messages-info">
            {labels?.singleExamLevelMessage}
          </CalciteInputMessage>
          {defaultProduct && selectedProduct !== defaultProduct && (
            <CalciteInputMessage
              scale="s"
              className="trainingvouchers-modal__messages-error"
              status="invalid"
              icon="information"
            >
              {labels?.examLevelMismatchMessage}
            </CalciteInputMessage>
          )}
        </div>
        <div className="trainingvouchers-modal__total" aria-live="polite">
          <div className="trainingvouchers-modal__total-amount">
            <span className="trainingvouchers-modal__total-label">{labels?.total}</span>
            <span className="trainingvouchers-modal__total-value">{formattedTotalPrice}</span>
          </div>
          <p className="trainingvouchers-modal__total-taxes">{labels?.applicableTaxes}</p>
        </div>
      </div>

      <div className="trainingvouchers-modal__policy">
        <CalciteLabel layout="inline">
          <CalciteCheckbox
            checked={policyAccepted}
            onChange={handlePolicyChange}
            className={`trainingvouchers-modal__policy-checkbox ${formState.fields?.policyAccepted?.errors?.[VALIDATION_ERROR_CODES.REQUIRED] && formState.fields?.policyAccepted?.dirty ? ' trainingvouchers-modal__policy-checkbox--invalid' : ''}`}
          />
          <span className="trainingvouchers-modal__policy-text required">
            {labels?.policyText ? parse(labels.policyText) : null}
          </span>
        </CalciteLabel>
        {formState.fields?.policyAccepted?.errors?.[VALIDATION_ERROR_CODES.REQUIRED]
          && formState.fields?.policyAccepted?.dirty && (
          <CalciteInputMessage
            scale="s"
            className="trainingvouchers-modal__messages-error"
            status="invalid"
            icon="exclamation-mark-triangle"
          >
            {labels?.policyError}
          </CalciteInputMessage>
        )}
      </div>
    </>
  );
};

TrainingVoucherContent.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      sku: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }),
  ),
  defaultProduct: PropTypes.string,
  currency: PropTypes.shape({
    currencyPlacement: PropTypes.oneOf(['before', 'after', null]),
    currencySymbol: PropTypes.string.isRequired,
    currencyCode: PropTypes.string.isRequired,
  }),
  formattedLocale: PropTypes.string,
  labels: PropTypes.shape({
    contentHeading: PropTypes.string,
    examLevel: PropTypes.string,
    selectExamLevel: PropTypes.string,
    quantity: PropTypes.string,
    singleExamLevelMessage: PropTypes.string,
    examLevelMismatchMessage: PropTypes.string,
    total: PropTypes.string,
    applicableTaxes: PropTypes.string,
    policyText: PropTypes.string,
    policyError: PropTypes.string,
  }),
  state: PropTypes.shape({
    quantity: PropTypes.number.isRequired,
    policyAccepted: PropTypes.bool.isRequired,
    selectedProduct: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  formState: PropTypes.shape({
    valid: PropTypes.bool.isRequired,
    dirty: PropTypes.bool.isRequired,
    fields: PropTypes.objectOf(
      PropTypes.shape({
        errors: PropTypes.object,
        valid: PropTypes.bool.isRequired,
        dirty: PropTypes.bool.isRequired,
      }),
    ).isRequired,
  }).isRequired,
};

export default TrainingVoucherContent;
